import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { api, apiGet } from "../../../Components/functions/Api";
import { toast } from "react-toastify";
import addDays from "date-fns/addDays";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import eachDayOfInterval from "date-fns/eachDayOfInterval";
import format from "date-fns/format";
import isSameDay from "date-fns/isSameDay";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import isValid from "date-fns/isValid";
import { PurpleButton } from "../../../Components/Buttons/PurpleButton";
import { BlackButton } from "../../../Components/Buttons/BlackButton";
import Modal from "react-responsive-modal";

export const HostCalendar = () => {
  const account = useSelector((state) => state.auth.account);
  const allNFTs = useSelector((state) => state.nft.nfts);
  
  // State management
  const [selectedProperty, setSelectedProperty] = useState("all");
  const [selectedDate, setSelectedDate] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showManualBlockModal, setShowManualBlockModal] = useState(false);
  const [manualBlockDates, setManualBlockDates] = useState({
    startDate: null,
    endDate: null,
    reason: ""
  });

  // Helper function to safely format dates
  const safeFormatDate = (date, formatString) => {
    if (!date) return "";
    const dateObj = date instanceof Date ? date : new Date(date);
    return isValid(dateObj) ? format(dateObj, formatString) : "";
  };
  
  // Generate 12 months from current month
  const months = useMemo(() => {
    const monthsArray = [];
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      monthsArray.push(date);
    }
    return monthsArray;
  }, []);

  // Fetch host properties
  const fetchProperties = useCallback(async () => {
    if (!account) {
      console.log("📅 [Calendar] No account, skipping property fetch");
      return;
    }
    
    console.log("📅 [Calendar] Fetching properties for account:", account);
    setLoading(true);
    
    try {
      // Use apiGet with query parameters
      console.log("📅 [Calendar] API URL: property/host/properties");
      console.log("📅 [Calendar] Params:", { wallet: account });
      
      const data = await apiGet("property/host/properties", {
        wallet: account
      });

      console.log("📅 [Calendar] Response data:", data);

      if (data && data.success && Array.isArray(data.properties)) {
        console.log("📅 [Calendar] Found properties:", data.properties.length);
        
        // Sort by highest price first
        const sortedProperties = data.properties
          .filter(p => p.short?.islisted)
          .sort((a, b) => (b.short?.price_per_day || 0) - (a.short?.price_per_day || 0));
        
        console.log("📅 [Calendar] Listed properties after sorting:", sortedProperties.length);
        setProperties(sortedProperties);
        
        // Auto-select highest price property
        if (sortedProperties.length > 0 && selectedProperty === "all") {
          console.log("📅 [Calendar] Auto-selecting highest price property:", sortedProperties[0].token_id);
          setSelectedProperty(sortedProperties[0].token_id);
        }
      } else {
        console.warn("📅 [Calendar] Unexpected response format:", data);
        setProperties([]);
      }
    } catch (error) {
      console.error("📅 [Calendar] Error fetching properties:", error);
      toast.error(`Failed to load properties: ${error.message}`);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [account, selectedProperty]);

  // Fetch blocked dates for selected property
  const fetchBlockedDates = useCallback(async () => {
    console.log("📅 [Calendar] Fetching blocked dates for property:", selectedProperty);
    console.log("📅 [Calendar] Total properties:", properties.length);
    
    if (!selectedProperty || selectedProperty === "all") {
      // Fetch all properties' blocked dates
      const allBlockedDates = [];
      for (const property of properties) {
        const dates = await getPropertyBlockedDates(property);
        allBlockedDates.push(...dates);
      }
      console.log("📅 [Calendar] Total blocked dates (all properties):", allBlockedDates.length);
      setBlockedDates(allBlockedDates);
    } else {
      // Fetch single property blocked dates
      const property = properties.find(p => p.token_id === selectedProperty);
      if (property) {
        const dates = await getPropertyBlockedDates(property);
        console.log("📅 [Calendar] Blocked dates for property:", dates.length);
        setBlockedDates(dates);
      } else {
        console.warn("📅 [Calendar] Property not found:", selectedProperty);
        setBlockedDates([]);
      }
    }
  }, [selectedProperty, properties]);

  // Helper to get blocked dates from a property
  const getPropertyBlockedDates = (property) => {
    const dates = [];
    
    // 1. Internal bookings (CodedEstate platform)
    // ✅ FILTER: Only include non-cancelled and non-failed bookings
    if (property.rentals?.rentals && Array.isArray(property.rentals.rentals)) {
      property.rentals.rentals.forEach(rental => {
        // Skip cancelled or failed bookings
        if (rental.cancelled || rental.sync_status === "failed") {
          return;
        }
        
        const startDate = new Date(parseInt(rental.renting_period[0]) * 1000);
        const endDate = new Date(parseInt(rental.renting_period[1]) * 1000);
        
        dates.push({
          startDate,
          endDate,
          type: "booking",
          source: "CodedEstate",
          guestName: rental.travelerProfile?.Name || "Guest",
          guestImage: rental.travelerProfile?.Avatar,
          guestWallet: rental.address,
          propertyId: property.token_id,
          propertyName: property.metaData?.buildingName,
          price: property.short?.price_per_day
        });
      });
    }
    
    // 2. External platform bookings (Airbnb, Booking.com, etc.)
    if (property.icalSync?.externalBlockedDates && Array.isArray(property.icalSync.externalBlockedDates)) {
      property.icalSync.externalBlockedDates.forEach(blocked => {
        dates.push({
          startDate: new Date(blocked.startDate),
          endDate: new Date(blocked.endDate),
          type: "external",
          source: blocked.platform || "External Platform",
          eventTitle: blocked.eventTitle,
          propertyId: property.token_id,
          propertyName: property.metaData?.buildingName,
          price: property.short?.price_per_day
        });
      });
    }
    
    // 3. Manual blocks
    if (property.manuallyBlockedDates && Array.isArray(property.manuallyBlockedDates)) {
      property.manuallyBlockedDates.forEach(block => {
        dates.push({
          startDate: new Date(block.startDate),
          endDate: new Date(block.endDate),
          type: "manual",
          source: "Manually Blocked",
          reason: block.reason,
          blockId: block._id,
          propertyId: property.token_id,
          propertyName: property.metaData?.buildingName,
          price: property.short?.price_per_day
        });
      });
    }
    
    return dates;
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (properties.length > 0) {
      fetchBlockedDates();
    }
  }, [selectedProperty, properties]);

  // Check if a date is blocked
  const getDateInfo = (date) => {
    return blockedDates.filter(blocked => {
      const dateToCheck = new Date(date);
      dateToCheck.setHours(0, 0, 0, 0);
      
      const start = new Date(blocked.startDate);
      start.setHours(0, 0, 0, 0);
      
      const end = new Date(blocked.endDate);
      end.setHours(0, 0, 0, 0);
      
      return dateToCheck >= start && dateToCheck <= end;
    });
  };

  // Handle manual block
  const handleManualBlock = async () => {
    if (!manualBlockDates.startDate || !manualBlockDates.endDate) {
      toast.error("Please select start and end dates");
      return;
    }

    if (!selectedProperty || selectedProperty === "all") {
      toast.error("Please select a specific property to block dates");
      return;
    }

    try {
      // Optimistically update the UI immediately
      const property = properties.find(p => p.token_id === selectedProperty);
      const newBlock = {
        startDate: manualBlockDates.startDate,
        endDate: manualBlockDates.endDate,
        type: "manual",
        source: "Manually Blocked",
        reason: manualBlockDates.reason || "Manually blocked by host",
        propertyId: selectedProperty,
        propertyName: property?.metaData?.buildingName,
        price: property?.short?.price_per_day
      };
      
      // Add the new block to blockedDates immediately for instant feedback
      setBlockedDates(prev => [...prev, newBlock]);
      
      // Close modal and reset form
      setShowManualBlockModal(false);
      setManualBlockDates({ startDate: null, endDate: null, reason: "" });
      
      // Make API call in background
      const response = await api("property/blockDate", {
        propertyId: selectedProperty,
        startDate: manualBlockDates.startDate.toISOString(),
        endDate: manualBlockDates.endDate.toISOString(),
        reason: manualBlockDates.reason || "Manually blocked by host",
        account: account
      });

      if (response.success) {
        toast.success("Dates blocked successfully");
        // Silently update properties in background without causing reload
        setProperties(prev => prev.map(p => {
          if (p.token_id === selectedProperty) {
            const updatedManuallyBlockedDates = [
              ...(p.manuallyBlockedDates || []),
              {
                startDate: manualBlockDates.startDate.toISOString(),
                endDate: manualBlockDates.endDate.toISOString(),
                reason: manualBlockDates.reason || "Manually blocked by host",
                _id: response.blockedDate?._id || Date.now().toString()
              }
            ];
            return {
              ...p,
              manuallyBlockedDates: updatedManuallyBlockedDates
            };
          }
          return p;
        }));
      } else {
        // If API call failed, remove the optimistic update
        setBlockedDates(prev => prev.filter(block => block !== newBlock));
        toast.error(response.error || "Failed to block dates");
      }
    } catch (error) {
      console.error("Error blocking dates:", error);
      toast.error("Failed to block dates");
      // Silently refetch blocked dates without full reload
      fetchBlockedDates();
    }
  };

  // Handle manual unblock
  const handleManualUnblock = async (block) => {
    try {
      console.log("🔓 [UNBLOCK] Attempting to unblock:", {
        propertyId: block.propertyId,
        startDate: block.startDate,
        endDate: block.endDate,
        blockId: block.blockId
      });

      // Create a unique identifier for this block
      const blockIdentifier = block.blockId || 
        `${block.propertyId}-${block.startDate.getTime()}-${block.endDate.getTime()}`;
      
      // Store the original block for potential restoration
      const originalBlock = { ...block };
      
      // Optimistically remove the block from UI immediately using proper comparison
      setBlockedDates(prev => {
        const filtered = prev.filter(b => {
          // Use blockId if available, otherwise compare dates and propertyId
          if (b.blockId && block.blockId) {
            return b.blockId !== block.blockId;
          }
          // Compare by propertyId and dates
          const bStart = b.startDate instanceof Date ? b.startDate.getTime() : new Date(b.startDate).getTime();
          const bEnd = b.endDate instanceof Date ? b.endDate.getTime() : new Date(b.endDate).getTime();
          const blockStart = block.startDate instanceof Date ? block.startDate.getTime() : new Date(block.startDate).getTime();
          const blockEnd = block.endDate instanceof Date ? block.endDate.getTime() : new Date(block.endDate).getTime();
          
          return !(b.propertyId === block.propertyId && 
                   b.type === "manual" &&
                   bStart === blockStart && 
                   bEnd === blockEnd);
        });
        console.log("🔓 [UNBLOCK] Filtered blocked dates:", {
          before: prev.length,
          after: filtered.length,
          removed: prev.length - filtered.length
        });
        return filtered;
      });
      
      // Normalize dates for API call
      // The backend uses overlap detection: unblockStartDate < existingEnd && unblockEndDate > existingStart
      // For single date blocks, we need to ensure the unblock range overlaps with the stored block
      const unblockStartDate = new Date(block.startDate);
      unblockStartDate.setHours(0, 0, 0, 0);
      
      let unblockEndDate = new Date(block.endDate);
      unblockEndDate.setHours(0, 0, 0, 0);
      
      // If it's a single date block (same day), we need to extend the range slightly
      // to ensure overlap detection works. Set endDate to end of the same day.
      if (unblockStartDate.getTime() === unblockEndDate.getTime()) {
        unblockEndDate = new Date(unblockEndDate);
        unblockEndDate.setHours(23, 59, 59, 999);
      } else {
        // For multi-day blocks, set to end of the end date
        unblockEndDate.setHours(23, 59, 59, 999);
      }
      
      console.log("🔓 [UNBLOCK] Sending API request:", {
        propertyId: block.propertyId,
        startDate: unblockStartDate.toISOString(),
        endDate: unblockEndDate.toISOString(),
        originalBlock: {
          startDate: block.startDate.toISOString(),
          endDate: block.endDate.toISOString()
        }
      });
      
      // Make API call in background
      const response = await api("property/unblockDate", {
        propertyId: block.propertyId,
        startDate: unblockStartDate.toISOString(),
        endDate: unblockEndDate.toISOString(),
        account: account
      });

      console.log("🔓 [UNBLOCK] API response:", response);

      if (response.success) {
        toast.success("Dates unblocked successfully");
        // Silently update properties in background without causing reload
        // Only update the specific property's manuallyBlockedDates
        setProperties(prev => prev.map(p => {
          if (p.token_id === block.propertyId && p.manuallyBlockedDates) {
            const normalizedBlockStart = block.startDate instanceof Date 
              ? block.startDate.getTime() 
              : new Date(block.startDate).getTime();
            const normalizedBlockEnd = block.endDate instanceof Date 
              ? block.endDate.getTime() 
              : new Date(block.endDate).getTime();
            
            const filtered = p.manuallyBlockedDates.filter(b => {
              const bStart = new Date(b.startDate).getTime();
              const bEnd = new Date(b.endDate).getTime();
              // Remove if blockId matches, or if dates and property match
              if (block.blockId && b._id) {
                return b._id !== block.blockId;
              }
              return !(bStart === normalizedBlockStart && bEnd === normalizedBlockEnd);
            });
            
            console.log("🔓 [UNBLOCK] Updated property manuallyBlockedDates:", {
              before: p.manuallyBlockedDates.length,
              after: filtered.length
            });
            
            return {
              ...p,
              manuallyBlockedDates: filtered
            };
          }
          return p;
        }));
      } else {
        // If API call failed, restore the block
        console.error("🔓 [UNBLOCK] API call failed, restoring block");
        setBlockedDates(prev => [...prev, originalBlock]);
        toast.error(response.error || "Failed to unblock dates");
      }
    } catch (error) {
      console.error("🔓 [UNBLOCK] Error unblocking dates:", error);
      // Restore the block on error
      setBlockedDates(prev => [...prev, block]);
      toast.error("Failed to unblock dates");
    }
  };

  // Handle date click - toggle block/unblock (Airbnb-style)
  const handleDateClick = async (date) => {
    // Normalize date to start of day (midnight) to avoid timezone issues
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    
    // Always set selected date to show info in sidebar
    setSelectedDate(normalizedDate);
    
    // Check if date is blocked and find manual blocks (can be toggled)
    const dateBlocks = getDateInfo(normalizedDate);
    const manualBlocks = dateBlocks.filter(block => block.type === "manual");
    const hasManualBlock = manualBlocks.length > 0;
    const isAvailable = dateBlocks.length === 0;
    
    // If clicking on a manually blocked date, unblock it (toggle)
    if (hasManualBlock && selectedProperty && selectedProperty !== "all") {
      // Unblock the first manual block for this date
      const blockToUnblock = manualBlocks[0];
      console.log("🔄 [DATE_CLICK] Toggling manual block:", {
        date: normalizedDate,
        block: blockToUnblock,
        hasBlockId: !!blockToUnblock.blockId,
        propertyId: blockToUnblock.propertyId
      });
      await handleManualUnblock(blockToUnblock);
      return;
    }
    
    // If date is available and a specific property is selected, block it directly
    if (isAvailable && selectedProperty && selectedProperty !== "all") {
      try {
        // Block single date - set endDate to end of day for proper overlap detection
        const property = properties.find(p => p.token_id === selectedProperty);
        const blockEndDate = new Date(normalizedDate);
        blockEndDate.setHours(23, 59, 59, 999); // End of day for proper unblock overlap detection
        
        const newBlock = {
          startDate: normalizedDate,
          endDate: blockEndDate,
          type: "manual",
          source: "Manually Blocked",
          reason: "Manually blocked by host",
          propertyId: selectedProperty,
          propertyName: property?.metaData?.buildingName,
          price: property?.short?.price_per_day
        };
        
        // Optimistically update the UI immediately
        setBlockedDates(prev => [...prev, newBlock]);
        toast.success("Date blocked successfully");
        
        // Make API call in background - send endDate as end of day
        const response = await api("property/blockDate", {
          propertyId: selectedProperty,
          startDate: normalizedDate.toISOString(),
          endDate: blockEndDate.toISOString(),
          reason: "Manually blocked by host",
          account: account
        });

        if (response.success) {
          // Update the block with blockId from API response
          const blockId = response.blockedDate?._id || response.blockId;
          if (blockId) {
            setBlockedDates(prev => prev.map(b => {
              // Update the block that matches this date and property
              if (b.propertyId === selectedProperty && 
                  b.type === "manual" &&
                  b.startDate.getTime() === normalizedDate.getTime() &&
                  b.endDate.getTime() === normalizedDate.getTime() &&
                  !b.blockId) {
                return { ...b, blockId };
              }
              return b;
            }));
          }
          
          // Silently update properties in background without causing reload
          // Add the new block to the property's manuallyBlockedDates
          setProperties(prev => prev.map(p => {
            if (p.token_id === selectedProperty) {
              const updatedManuallyBlockedDates = [
                ...(p.manuallyBlockedDates || []),
                {
                  startDate: normalizedDate.toISOString(),
                  endDate: blockEndDate.toISOString(), // Use end of day
                  reason: "Manually blocked by host",
                  _id: blockId || Date.now().toString()
                }
              ];
              return {
                ...p,
                manuallyBlockedDates: updatedManuallyBlockedDates
              };
            }
            return p;
          }));
        } else {
          // If API call failed, remove the optimistic update
          setBlockedDates(prev => prev.filter(b => {
            // Remove block that matches this date and property
            return !(b.propertyId === selectedProperty && 
                     b.type === "manual" &&
                     b.startDate.getTime() === normalizedDate.getTime() &&
                     b.endDate.getTime() === normalizedDate.getTime());
          }));
          toast.error(response.error || "Failed to block date");
        }
      } catch (error) {
        console.error("Error blocking date:", error);
        toast.error("Failed to block date");
        // Silently refetch blocked dates without full reload
        fetchBlockedDates();
      }
    } else if (isAvailable && (!selectedProperty || selectedProperty === "all")) {
      // If "all properties" is selected, show a message
      toast.info("Please select a specific property to block dates");
    }
    // If date is blocked by booking or external platform, just show info (no toggle)
  };

  // Render calendar for a month
  const renderMonth = (monthDate) => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
    
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    const weeks = [];
    
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <div key={monthDate.toISOString()} className="mb-6 sm:mb-8">
        {/* Month header */}
        <div className="sticky top-0 bg-white z-10 py-2 sm:py-3 border-b border-gray-200 mb-2">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            {format(monthDate, "MMMM yyyy")}
          </h3>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-2">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <div key={day} className="text-center text-xs sm:text-sm font-medium text-gray-500 py-1 sm:py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 gap-0.5 sm:gap-1">
            {week.map((day) => {
              const isCurrentMonth = day.getMonth() === monthDate.getMonth();
              const isPast = day < new Date(new Date().setHours(0, 0, 0, 0));
              const dateBlocks = getDateInfo(day);
              const isBlocked = dateBlocks.length > 0;
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              
              return (
                <div
                  key={day.toISOString()}
                  onClick={() => !isPast && handleDateClick(day)}
                  className={`
                    relative min-h-[60px] sm:min-h-[80px] p-1 sm:p-2 rounded-md sm:rounded-lg border-2 transition-all cursor-pointer
                    ${!isCurrentMonth ? "bg-gray-50 text-gray-400" : "bg-white"}
                    ${isPast ? "opacity-50 cursor-not-allowed" : ""}
                    ${isSelected ? "border-purple-500 bg-purple-50 shadow-lg" : "border-gray-200"}
                    ${!isPast && !isBlocked ? "hover:bg-gray-50 hover:border-gray-300" : ""}
                  `}
                >
                  {/* Date number */}
                  <div className={`text-xs sm:text-sm font-medium ${isCurrentMonth ? "text-gray-900" : "text-gray-400"}`}>
                    {format(day, "d")}
                  </div>

                  {/* Blocked date indicators */}
                  {isBlocked && (
                    <div className="mt-0.5 sm:mt-1 space-y-0.5 sm:space-y-1">
                      {dateBlocks.slice(0, 2).map((block, index) => (
                        <div
                          key={index}
                          className={`
                            text-[10px] sm:text-xs px-1 sm:px-2 py-0.5 sm:py-1 rounded truncate flex items-center gap-0.5 sm:gap-1
                            ${block.type === "booking" ? "bg-purple-100 text-purple-700" : ""}
                            ${block.type === "external" ? "bg-blue-100 text-blue-700" : ""}
                            ${block.type === "manual" ? "bg-gray-100 text-gray-700" : ""}
                          `}
                        >
                          {block.type === "booking" && block.guestImage && (
                            <img src={block.guestImage} alt="" className="w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0" />
                          )}
                          <span className="truncate">
                            {block.type === "booking" ? (block.guestName?.substring(0, 8) || "Guest") : block.source?.substring(0, 8) || "Blocked"}
                          </span>
                        </div>
                      ))}
                      {dateBlocks.length > 2 && (
                        <div className="text-[10px] sm:text-xs text-gray-500 px-1 sm:px-2">
                          +{dateBlocks.length - 2}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="h-full w-full flex flex-col bg-gray-50">
      {/* Fixed Header */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 md:px-6 py-3 sm:py-4 sticky top-0 z-20 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Calendar & Blocked Dates</h1>
          
          {/* Property Selector */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
            <div className="w-full sm:min-w-[250px] md:min-w-[300px] relative">
              <select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 pr-8 sm:pr-10 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-xs sm:text-sm font-medium appearance-none cursor-pointer bg-white hover:border-gray-400 transition-colors"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  backgroundSize: '1rem'
                }}
              >
                <option value="all">All Properties</option>
                {properties.map((property) => (
                  <option key={property.token_id} value={property.token_id}>
                    {property.metaData?.buildingName || "Unnamed Property"} • {property.metaData?.address?.city || "Location"} • {property.short?.price_per_day || 0} USDC/night
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Responsive Layout */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Calendar Section - Scrollable, Hidden on mobile when date selected */}
        <div className={`flex-1 overflow-y-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4 ${selectedDate ? 'hidden lg:block' : 'block'}`}>
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mb-4"></div>
              <p className="text-gray-600 text-sm sm:text-base">Loading your properties...</p>
            </div>
          ) : properties.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 px-4">
              <svg className="w-16 sm:w-20 h-16 sm:h-20 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-500 text-base sm:text-lg font-medium mb-2 text-center">No Listed Properties Found</p>
              <p className="text-gray-400 text-xs sm:text-sm mb-4 text-center">You need to have at least one listed property to view the calendar</p>
              <PurpleButton
                text="Go to My Properties"
                onClick={() => window.location.href = "/dashboard/host/nfts"}
                className="w-full sm:w-auto"
              />
            </div>
          ) : (
            <div className="max-w-5xl mx-auto">
              {months.map(month => renderMonth(month))}
            </div>
          )}
        </div>

        {/* Date Details Sidebar - Full width on mobile when date selected, sidebar on desktop */}
        <div className={`bg-white border-t lg:border-l border-gray-200 overflow-y-auto ${selectedDate ? 'block' : 'hidden lg:block'} lg:w-80`}>
          <div className="p-4 sm:p-6">
            {/* Back button for mobile */}
            {selectedDate && (
              <div className="lg:hidden mb-4">
                <button
                  onClick={() => setSelectedDate(null)}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Calendar
                </button>
              </div>
            )}
            
            {selectedDate ? (
              <>
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                    {format(selectedDate, "EEEE, MMMM d, yyyy")}
                  </h3>
                  <div className="text-xs sm:text-sm text-gray-500">
                    {getDateInfo(selectedDate).length > 0 ? "Blocked" : "Available"}
                  </div>
                </div>

                {/* Blocked dates info */}
                {getDateInfo(selectedDate).length > 0 ? (
                  <div className="space-y-3 sm:space-y-4">
                    <h4 className="font-medium text-gray-900 text-sm sm:text-base">Bookings & Blocks</h4>
                    {getDateInfo(selectedDate).map((block, index) => (
                      <div
                        key={index}
                        className={`
                          p-3 sm:p-4 rounded-lg border-l-4
                          ${block.type === "booking" ? "border-purple-500 bg-purple-50" : ""}
                          ${block.type === "external" ? "border-blue-500 bg-blue-50" : ""}
                          ${block.type === "manual" ? "border-gray-500 bg-gray-50" : ""}
                        `}
                      >
                        <div className="flex items-start gap-2 sm:gap-3">
                          {block.guestImage && (
                            <img
                              src={block.guestImage}
                              alt={block.guestName}
                              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm sm:text-base">
                              {block.type === "booking" && block.guestName}
                              {block.type === "external" && block.source}
                              {block.type === "manual" && "Manually Blocked"}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 mt-1">
                              {safeFormatDate(block.startDate, "MMM d")} - {safeFormatDate(block.endDate, "MMM d, yyyy")}
                            </div>
                            {block.propertyName && (
                              <div className="text-xs sm:text-sm text-gray-500 mt-1">
                                {block.propertyName}
                              </div>
                            )}
                            {block.reason && (
                              <div className="text-xs sm:text-sm text-gray-500 mt-1">
                                Reason: {block.reason}
                              </div>
                            )}
                            {block.type === "booking" && (
                              <div className="text-xs sm:text-sm text-gray-500 mt-1 break-all">
                                Guest: {block.guestWallet?.substring(0, 10)}...
                              </div>
                            )}
                            {block.type === "manual" && (
                              <div className="mt-3">
                                <BlackButton
                                  text="Unblock Dates"
                                  onClick={() => handleManualUnblock(block)}
                                  className="w-full sm:w-auto"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="text-center py-6 sm:py-8">
                      <div className="text-gray-400 mb-4">
                        <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <p className="text-gray-600 mb-2 text-sm sm:text-base font-medium">This date is available</p>
                      {selectedProperty && selectedProperty !== "all" ? (
                        <p className="text-gray-500 text-xs sm:text-sm mb-4">Click on the date in the calendar to block it</p>
                      ) : (
                        <p className="text-gray-500 text-xs sm:text-sm mb-4">Select a property to block dates</p>
                      )}
                    </div>
                  </>
                )}

                {/* Legend */}
                <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-3 text-sm sm:text-base">Legend</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 sm:w-4 sm:h-4 bg-purple-500 rounded flex-shrink-0"></div>
                      <span className="text-xs sm:text-sm text-gray-600">Booking (CodedEstate)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 sm:w-4 sm:h-4 bg-blue-500 rounded flex-shrink-0"></div>
                      <span className="text-xs sm:text-sm text-gray-600">External Platform</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gray-500 rounded flex-shrink-0"></div>
                      <span className="text-xs sm:text-sm text-gray-600">Manually Blocked</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-6 sm:py-8">
                <div className="text-gray-400 mb-4">
                  <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm sm:text-base">Click on a date to see details</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Manual Block Modal */}
      <Modal
        open={showManualBlockModal}
        onClose={() => setShowManualBlockModal(false)}
        center
        classNames={{
          modal: "rounded-lg p-4 sm:p-6 max-w-md w-full mx-4"
        }}
      >
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Block Dates Manually</h2>
        
        <div className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={manualBlockDates.startDate ? safeFormatDate(manualBlockDates.startDate, "yyyy-MM-dd") : ""}
              onChange={(e) => {
                const selectedDate = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                setManualBlockDates({
                  ...manualBlockDates,
                  startDate: selectedDate
                });
              }}
              className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={manualBlockDates.endDate ? safeFormatDate(manualBlockDates.endDate, "yyyy-MM-dd") : ""}
              onChange={(e) => {
                const selectedDate = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                setManualBlockDates({
                  ...manualBlockDates,
                  endDate: selectedDate
                });
              }}
              className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Reason (Optional)
            </label>
            <textarea
              value={manualBlockDates.reason}
              onChange={(e) => setManualBlockDates({
                ...manualBlockDates,
                reason: e.target.value
              })}
              placeholder="e.g., Property maintenance, personal use..."
              rows={3}
              className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-6">
            <BlackButton
              text="Cancel"
              onClick={() => setShowManualBlockModal(false)}
              className="w-full sm:w-auto"
            />
            <PurpleButton
              text="Block Dates"
              onClick={handleManualBlock}
              className="w-full sm:w-auto"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};
